import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { manhwaList } from '@/mocks/manhwa';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const manhwa = manhwaList.find((m) => m.id === Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!manhwa) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-4">抱歉，您要查看的漫画不存在或已被移除。</p>
            <Link to="/category/all" className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
              返回漫画列表
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedManhwa = manhwaList
    .filter((m) => m.id !== manhwa.id && m.category === manhwa.category)
    .slice(0, 4);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: manhwa.category === 'romance' ? '恋爱甜宠' : manhwa.category === 'fantasy' ? '奇幻冒险' : manhwa.category === 'thriller' ? '悬疑惊悚' : manhwa.category === 'urban' ? '都市职场' : '全部作品', path: `/category/${manhwa.category}` },
    { label: manhwa.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manhwa.title} - 韩国漫画免费在线阅读`,
    description: manhwa.description,
    author: {
      '@type': 'Person',
      name: manhwa.author,
    },
    datePublished: manhwa.chapters_list[0]?.date || '2025-01-01',
    dateModified: manhwa.updatedAt,
    inLanguage: 'zh-CN',
    url: `http://911hl.cn/article/${manhwa.id}`,
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫星球',
    url: 'http://911hl.cn/',
  };

  return (
    <>
      <SEOHead
        title={`${manhwa.title} - 韩漫免费在线阅读 | 韩漫星球`}
        description={manhwa.description}
        keywords={`${manhwa.title},韩国漫画,韩漫免费阅读,${manhwa.author},下拉式韩漫,韩漫在线`}
        canonicalPath={`/article/${manhwa.id}`}
        ogType="article"
        schemaJson={[orgSchema, articleSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <div className="pt-20 pb-10 md:pb-14 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="mb-6">
                <Breadcrumb items={breadcrumbItems} />
              </div>

              <div className="flex flex-col lg:flex-row gap-8 md:gap-5">
                <div className="lg:w-[35%] flex-shrink-0">
                  <div className="relative rounded-xl overflow-hidden aspect-[3/4] max-w-sm mx-auto lg:max-w-none">
                    <img
                      src={manhwa.coverImage}
                      alt={manhwa.title}
                      title={`${manhwa.title} - 韩国漫画免费在线阅读`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="lg:w-[65%]">
                  <div className="flex items-center flex-wrap gap-2 mb-3">
                    {manhwa.status === '连载中' ? (
                      <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">
                        连载中
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                        已完成
                      </span>
                    )}
                    <span className="text-xs text-foreground-500 flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i>
                      {manhwa.rating} 分
                    </span>
                    <span className="text-xs text-foreground-500">
                      {manhwa.views.toLocaleString()} 次阅读
                    </span>
                    <span className="text-xs text-foreground-500">
                      共 {manhwa.chapters} 话
                    </span>
                  </div>

                  <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
                    {manhwa.title}
                  </h1>

                  <p className="text-sm text-foreground-600 mb-4">
                    作者：<strong>{manhwa.author}</strong>
                  </p>

                  <p className="text-sm text-foreground-700 leading-relaxed mb-6 max-w-2xl">
                    {manhwa.description}
                  </p>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-8">
                    <button className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full text-sm font-medium cursor-pointer transition-colors inline-flex items-center gap-2">
                      <i className="ri-play-circle-line"></i>
                      开始阅读
                    </button>
                    <button className="whitespace-nowrap border border-background-300 text-foreground-700 hover:bg-background-100 px-8 py-3 rounded-full text-sm font-medium cursor-pointer transition-colors inline-flex items-center gap-2">
                      <i className="ri-bookmark-line"></i>
                      加入书架
                    </button>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-foreground-500 flex-wrap">
                    <span>最后更新：{manhwa.updatedAt}</span>
                    <span>总章节：{manhwa.chapters} 话</span>
                  </div>
                </div>
              </div>

              <div className="mt-12 md:mt-14">
                <div className="text-center mb-8">
                  <span className="inline-block px-4 py-1.5 rounded-full border border-primary-300 text-primary-600 text-sm mb-4 whitespace-nowrap">
                    章节目录
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                    章节目录
                  </h2>
                  <p className="text-foreground-600 text-sm">共 {manhwa.chapters} 话 · 持续更新中</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5 max-w-4xl mx-auto">
                  {Array.from({ length: Math.min(manhwa.chapters, 15) }).map((_, i) => {
                    const chapterNum = i + 1;
                    const chapterData = manhwa.chapters_list.find((c) => c.num === chapterNum);
                    return (
                      <div
                        key={i}
                        className="group flex items-center gap-3 p-4 rounded-lg bg-background-100 hover:bg-primary-500 transition-colors duration-300 cursor-pointer"
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-background-200 rounded-lg group-hover:bg-background-50/20 transition-colors flex-shrink-0">
                          <i className="ri-file-text-line text-primary-500 group-hover:text-background-50 transition-colors"></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground-950 group-hover:text-background-50 transition-colors whitespace-nowrap">
                            第 {chapterNum} 话
                          </p>
                          <p className="text-xs text-foreground-500 group-hover:text-background-50/70 transition-colors truncate">
                            {chapterData?.title || '精彩继续'}
                          </p>
                        </div>
                        <div className="ml-auto flex-shrink-0">
                          <span className="text-xs text-foreground-400 group-hover:text-background-50/60 transition-colors">
                            {chapterData?.date || manhwa.updatedAt}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {manhwa.chapters > 15 && (
                  <div className="text-center mt-6">
                    <button className="whitespace-nowrap text-primary-500 hover:text-primary-600 text-sm font-medium cursor-pointer inline-flex items-center gap-1">
                      查看全部 {manhwa.chapters} 话 <i className="ri-arrow-down-s-line"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {relatedManhwa.length > 0 && (
            <section className="py-14 md:py-20 px-4 md:px-6 bg-foreground-950">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-10 md:mb-5">
                  <div>
                    <span className="inline-block px-4 py-1.5 rounded-full border border-primary-400/40 text-primary-400 text-sm mb-4 whitespace-nowrap">
                      同类型推荐
                    </span>
                    <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold font-heading text-background-50">
                      你可能也喜欢
                    </h2>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {relatedManhwa.map((m) => (
                    <Link
                      key={m.id}
                      to={`/article/${m.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                        <img
                          src={m.coverImage}
                          alt={m.title}
                          title={`${m.title} - 韩国漫画免费在线`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h3 className="text-sm font-semibold text-background-50 group-hover:text-primary-400 transition-colors line-clamp-1">
                        {m.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-foreground-400">{m.author}</span>
                        <span className="text-foreground-600">·</span>
                        <span className="text-xs text-accent-400 flex items-center gap-1">
                          <i className="ri-star-fill text-[10px]"></i>
                          {m.rating}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}